'use client';

import React from 'react';
import { Section, Container, SectionHeader, GridLayoutWithAside } from '@layout';
import { Card, Heading } from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';
import { IconUsers } from '@icons';

import type { GridStrings } from './GridStrings';

interface GridProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  strings: GridStrings;
}

const Grid: React.FC<GridProps> = ({
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
  strings: s,
}) => {
  return (
    <Section sectionID={sectionID} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <div>
          <Heading
            as='h3'
            title={s.headingTitle}
            descriptionClassName='sub full'
            description={[s.headingDescription[0], s.headingDescription[1]]}
          />
        </div>

        <div className='mobile-hide'>
          <Heading as='h4' title={s.balancedFourTitle} />
          <div className='grid-4'>
            <Card>
              <p>{s.cardDefault}</p>
            </Card>
            <Card className='card-1'>
              <p>{s.cardSurface2}</p>
            </Card>
            <Card className='card-2'>
              <p>{s.cardSurface3}</p>
            </Card>
            <Card className='card-3'>
              <p>{s.cardSurface4}</p>
            </Card>
          </div>
        </div>

        <div className='mobile-hide'>
          <Heading as='h4' title={s.contentFriendlyTitle} />
          <div className='grid-3'>
            <Card>
              <p>{s.cardDefault}</p>
            </Card>
            <Card className='card-1'>
              <p>{s.cardSurface2}</p>
            </Card>
            <Card className='card-2'>
              <p>{s.cardSurface3}</p>
            </Card>
          </div>
        </div>

        <div>
          <Heading as='h4'>{s.asideSectionTitle}</Heading>
          <GridLayoutWithAside
            gridAsideId='grid-with-aside'
            toggleButtonClass='btn-primary'
            asideNavLabel={s.sidebarNavigationTitle}
            toggleButtonLabel={s.toggleShowAside}
            slideOverId='aside-slideover'
            asideNavContent={
              <div className='card card-0'>
                <p className='h4'>{s.sidebarNavigationTitle}</p>
                <p>{s.asideDesktopBody1}</p>
                <p>{s.asideDesktopBody2}</p>
              </div>
            }
            asideContent={
              <div className='card card-0'>
                <p className='h4'>{s.sidebarNavigationTitle}</p>
                <p>{s.asideDesktopBody1}</p>
                <p>{s.asideDesktopBody2}</p>
              </div>
            }
            sectionContent={
              <Section sectionID='grid-with-aside-main'>
                <SectionHeader
                  title={s.articleHeaderTitle}
                  buttonLinkText={s.headingButton}
                  buttonLinkClass='btn-primary'
                  buttonLinkAs='a'
                  buttonLinkHref='#'
                  buttonLinkTarget='_blank'
                  buttonLinkRel='noopener'
                  buttonLinkIcon={<IconUsers />}
                  buttonLinkIconPosition='right'
                />
                <article className='card card-1'>
                  <div className='h1'>{s.heading1}</div>
                  <div className='h2'>{s.heading2}</div>
                  <div className='h3'>{s.heading3}</div>
                  <div className='h4'>{s.heading4}</div>
                  <div className='h5'>{s.heading5}</div>
                  <div className='h6'>{s.heading6}</div>
                  <p>{s.articleCardBody}</p>
                </article>
                <article className='card card-2'>
                  <p>{s.articleCardBody}</p>
                </article>
              </Section>
            }
          />
        </div>

        <div>
          <Heading as='h4'>{s.collapsibleAsideSectionTitle}</Heading>
          <GridLayoutWithAside
            collapsible
            gridAsideId='grid-with-aside-collapsible'
            toggleButtonClass='btn-primary'
            asideNavLabel={s.sidebarNavigationTitle}
            toggleButtonLabel={s.toggleShowAside}
            collapseButtonLabel={s.collapseAsideLabel}
            expandButtonLabel={s.expandAsideLabel}
            slideOverId='aside-slideover-collapsible'
            asideNavContent={
              <div className='card card-0'>
                <p className='h4'>{s.sidebarNavigationTitle}</p>
                <p>{s.sidebarDesktopBody}</p>
                <p>{s.asideDesktopBody2}</p>
              </div>
            }
            asideContent={
              <div className='card card-0'>
                <p className='h4'>{s.sidebarNavigationTitle}</p>
                <p>{s.sidebarDesktopBody}</p>
                <p>{s.asideDesktopBody2}</p>
              </div>
            }
            sectionContent={
              <Section sectionID='grid-with-aside-collapsible-main'>
                <SectionHeader
                  title={s.collapsibleArticleHeaderTitle}
                  buttonLinkText={s.headingButton}
                  buttonLinkClass='btn-primary'
                  buttonLinkAs='a'
                  buttonLinkHref='#'
                  buttonLinkTarget='_blank'
                  buttonLinkRel='noopener'
                  buttonLinkIcon={<IconUsers />}
                  buttonLinkIconPosition='right'
                />
                <article className='card card-1'>
                  <p>{s.articleCardBody}</p>
                </article>
              </Section>
            }
          />
        </div>
        <MarkupSnippet
          {...markupCopyLabels(false)}
          code={`<div class="grid-4">
  <article class="card card-1">Column</article>
  <article class="card card-2">Column</article>
  <article class="card card-3">Column</article>
  <article class="card">Column</article>
</div>
<div class="grid-3">
  <article class="card">Column</article>
  <article class="card">Column</article>
  <article class="card">Column</article>
</div>`}
        />
      </Container>
    </Section>
  );
};

export default Grid;
